import { measureNode } from '../utils.js';

export class SnapManager {
  static SNAP_THRESHOLD = 5;

  static getGridOffset(containerNode) {
    let offsetX = 0;
    let offsetY = 0;
    if (containerNode && containerNode.attachedComponents) {
      const gridComp = containerNode.attachedComponents.find(c => c.type === 'Grid');
      if (gridComp && gridComp.props?.enabled !== false) {
        const padding = Number(gridComp.props.padding) || 0;
        offsetX = padding;
        offsetY = padding;
      }
    }
    return { offsetX, offsetY };
  }

  static snapToGrid(value, gridSize, offset = 0) {
    return Math.round((value - offset) / gridSize) * gridSize + offset;
  }

  static snapPositionToGrid(x, y, gridSize, offsetX = 0, offsetY = 0) {
    return {
      x: this.snapToGrid(x, gridSize, offsetX),
      y: this.snapToGrid(y, gridSize, offsetY)
    };
  }

  static snapRectToGrid(rect, gridSize, offsetX = 0, offsetY = 0) {
    return {
      x: this.snapToGrid(rect.x, gridSize, offsetX),
      y: this.snapToGrid(rect.y, gridSize, offsetY),
      width: Math.max(gridSize, this.snapToGrid(rect.width, gridSize)),
      height: Math.max(gridSize, this.snapToGrid(rect.height, gridSize))
    };
  }

  static findSnapLines(targetRect, candidateNodes, rootW, rootH, axis = null) {
    const threshold = this.SNAP_THRESHOLD;
    const snapLines = [];
    let bestDX = Infinity;
    let bestDY = Infinity;
    let snapX = null;
    let snapY = null;

    // Target edges: left, center, right for X; top, middle, bottom for Y
    // Use raw values to calculate precise delta
    const myLeft = targetRect.x;
    const myRight = targetRect.x + targetRect.width;
    const myCenterX = targetRect.x + targetRect.width / 2;
    const myTop = targetRect.y;
    const myBottom = targetRect.y + targetRect.height;
    const myCenterY = targetRect.y + targetRect.height / 2;

    // Add canvas/root boundaries as snap targets
    const canvasBoundaries = {
      left: 0,
      right: rootW,
      centerX: rootW / 2,
      top: 0,
      bottom: rootH,
      centerY: rootH / 2
    };

    // Check against canvas boundaries first
    if (axis !== 'y') {
      const xBoundaryPairs = [
        [myLeft, canvasBoundaries.left], [myLeft, canvasBoundaries.right], [myLeft, canvasBoundaries.centerX],
        [myRight, canvasBoundaries.left], [myRight, canvasBoundaries.right], [myRight, canvasBoundaries.centerX],
        [myCenterX, canvasBoundaries.left], [myCenterX, canvasBoundaries.right], [myCenterX, canvasBoundaries.centerX]
      ];

      xBoundaryPairs.forEach(([mine, boundary]) => {
        const diff = boundary - mine;
        if (Math.abs(diff) < threshold && Math.abs(diff) < Math.abs(bestDX)) {
          bestDX = diff;
          snapX = { x: boundary, type: 'vertical', isBoundary: true };
        }
      });
    }

    if (axis !== 'x') {
      const yBoundaryPairs = [
        [myTop, canvasBoundaries.top], [myTop, canvasBoundaries.bottom], [myTop, canvasBoundaries.centerY],
        [myBottom, canvasBoundaries.top], [myBottom, canvasBoundaries.bottom], [myBottom, canvasBoundaries.centerY],
        [myCenterY, canvasBoundaries.top], [myCenterY, canvasBoundaries.bottom], [myCenterY, canvasBoundaries.centerY]
      ];

      yBoundaryPairs.forEach(([mine, boundary]) => {
        const diff = boundary - mine;
        if (Math.abs(diff) < threshold && Math.abs(diff) < Math.abs(bestDY)) {
          bestDY = diff;
          snapY = { y: boundary, type: 'horizontal', isBoundary: true };
        }
      });
    }

    candidateNodes.forEach(other => {
      // Expect other to have a .rect property (measured global rect)
      const otherRect = other.rect;
      if (!otherRect) return;

      const otherLeft = otherRect.x;
      const otherTop = otherRect.y;
      const otherRight = otherRect.x + otherRect.width;
      const otherBottom = otherRect.y + otherRect.height;
      const otherCenterX = (otherLeft + otherRight) / 2;
      const otherCenterY = (otherTop + otherBottom) / 2;

      // Check X (Vertical Lines)
      if (axis !== 'y') {
        const xPairs = [
          [myLeft, otherLeft], [myLeft, otherRight], [myLeft, otherCenterX],
          [myRight, otherLeft], [myRight, otherRight], [myRight, otherCenterX],
          [myCenterX, otherLeft], [myCenterX, otherRight], [myCenterX, otherCenterX]
        ];

        xPairs.forEach(([mine, theirs]) => {
          const diff = theirs - mine;
          if (Math.abs(diff) < threshold && Math.abs(diff) < Math.abs(bestDX)) {
            bestDX = diff;
            snapX = { x: theirs, type: 'vertical', nodeId: other.id, rect: otherRect };
          }
        });
      }

      // Check Y (Horizontal Lines)
      if (axis !== 'x') {
        const yPairs = [
          [myTop, otherTop], [myTop, otherBottom], [myTop, otherCenterY],
          [myBottom, otherTop], [myBottom, otherBottom], [myBottom, otherCenterY],
          [myCenterY, otherTop], [myCenterY, otherBottom], [myCenterY, otherCenterY]
        ];

        yPairs.forEach(([mine, theirs]) => {
          const diff = theirs - mine;
          if (Math.abs(diff) < threshold && Math.abs(diff) < Math.abs(bestDY)) {
            bestDY = diff;
            snapY = { y: theirs, type: 'horizontal', nodeId: other.id, rect: otherRect };
          }
        });
      }
    });

    let deltaX = 0;
    let deltaY = 0;

    if (snapX) {
      // Use bestDX which already contains the correct delta for the matched edge
      deltaX = bestDX;
      snapLines.push({ 
        x: snapX.x, 
        vertical: true, 
        nodeId: snapX.nodeId,
        rect: snapX.rect,
        isBoundary: snapX.isBoundary 
      });
    }
    if (snapY) {
      // Use bestDY which already contains the correct delta for the matched edge
      deltaY = bestDY;
      snapLines.push({ 
        y: snapY.y, 
        vertical: false,
        nodeId: snapY.nodeId,
        rect: snapY.rect,
        isBoundary: snapY.isBoundary
      });
    }

    return { deltaX, deltaY, snapLines };
  }

  static applySnapToPosition(targetX, targetY, targetW, targetH, settings, selectedNodeIds, activeNodes, rootW, rootH, containerNode, axis = null, activeBreakpoint = 'default') {
    let newX = targetX;
    let newY = targetY;
    let snapLines = [];

    // Grid Snapping
    if (settings.snapToGrid) {
      const gridSize = settings.gridSize || 10;
      const { offsetX, offsetY } = this.getGridOffset(containerNode);
      const snapped = this.snapPositionToGrid(targetX, targetY, gridSize, offsetX, offsetY);
      newX = snapped.x;
      newY = snapped.y;
    }

    // Object Snapping
    if (settings.snapToObjects) {
      // Filter out selected nodes, container node, and child nodes of selected nodes
      const selectedSet = new Set(selectedNodeIds);
      const childrenOfSelected = new Set();

      // Collect all children of selected nodes
      const collectChildren = (nodeId) => {
        activeNodes.forEach(n => {
          if (n.parentId === nodeId) {
            childrenOfSelected.add(n.id);
            collectChildren(n.id);
          }
        });
      };

      selectedNodeIds.forEach(id => collectChildren(id));

      const candidateNodes = activeNodes
        .filter(n => !selectedSet.has(n.id) && !childrenOfSelected.has(n.id))
        .map(n => ({
          ...n,
          rect: measureNode(n.id, activeNodes, rootW, rootH, activeBreakpoint)
        }));

      // Calculate Container Offset for Global conversion
      const containerRect = containerNode 
          ? measureNode(containerNode.id, activeNodes, rootW, rootH, activeBreakpoint)
          : { x: 0, y: 0 };
      
      const { offsetX, offsetY } = this.getGridOffset(containerNode);

      // Convert target (Local) to Global for comparison
      // Local (newX) is relative to Padding Box (standard CSS absolute positioning)
      // ContainerRect is Border Box.
      // Global = ContainerRect.x + (Border?) + Padding + newX.
      // We assume Border is negligible or included in standard measurements for now, 
      // but Padding (offsetX) is critical.
      const globalTargetRect = {
        x: newX + containerRect.x + offsetX,
        y: newY + containerRect.y + offsetY,
        width: targetW,
        height: targetH
      };

      const snapResult = this.findSnapLines(globalTargetRect, candidateNodes, rootW, rootH, axis);
      newX += snapResult.deltaX;
      newY += snapResult.deltaY;
      snapLines = snapResult.snapLines;
    }

    return { x: newX, y: newY, snapLines };
  }

  static snapResizeToGrid(rect, handle, gridSize, containerNode = null) {
    const { offsetX, offsetY } = this.getGridOffset(containerNode);
    let { x, y, width, height } = rect;

    if (handle.includes('e')) {
      const currentRight = x + width;
      const snappedRight = this.snapToGrid(currentRight, gridSize, offsetX);
      width = snappedRight - x;
    }
    if (handle.includes('s')) {
      const currentBottom = y + height;
      const snappedBottom = this.snapToGrid(currentBottom, gridSize, offsetY);
      height = snappedBottom - y;
    }
    if (handle.includes('w')) {
      const snappedX = this.snapToGrid(x, gridSize, offsetX);
      width = (x + width) - snappedX;
      x = snappedX;
    }
    if (handle.includes('n')) {
      const snappedY = this.snapToGrid(y, gridSize, offsetY);
      height = (y + height) - snappedY;
      y = snappedY;
    }

    width = Math.max(gridSize, width);
    height = Math.max(gridSize, height);

    return { x, y, width, height };
  }
}